/**
 * Правки режиссёра в трансляции MLBB (overrides): черновик формы, PATCH-дифф и предпросмотр display.
 * Без зависимостей от Vue — покрыто тестами в tests/unit/mlbbBroadcastOverrides.spec.js.
 *
 * Формат бэка: {title, casters[], teams: {team_id: {name, short_name, logo}},
 * players: {role_id: {name, photo, hero_id}}, heroes: {наш id героя: {image}}}.
 * PATCH сливает переданное с сохранённым: null в поле снимает правку, null у сущности — все её правки.
 */
import {heroCatalog, logoUrl} from '@/common/mlbbBroadcast'

export const CASTER_SLOTS = 2

const ENTITY_FIELDS = {
    teams: ['name', 'short_name', 'logo'],
    players: ['name', 'photo', 'hero_id'],
    heroes: ['image'],
}

// в форме: строки без пробелов по краям ('' — правки нет), hero_id — число или null
function norm(field, value) {
    if (field === 'hero_id') return Number(value) || null
    return typeof value === 'string' ? value.trim() : (value ? String(value) : '')
}

const isSet = (field, value) => (field === 'hero_id' ? !!value : value !== '')

function entitiesDraft(map, fields) {
    const out = {}
    Object.entries(map || {}).forEach(([key, entity]) => {
        if (!entity) return
        out[key] = {}
        fields.forEach((f) => {
            out[key][f] = norm(f, entity[f])
        })
    })
    return out
}

/** Пустая правка сущности для формы */
export function emptyEntity(kind) {
    const out = {}
    ENTITY_FIELDS[kind].forEach((f) => {
        out[f] = f === 'hero_id' ? null : ''
    })
    return out
}

/** Сохранённые overrides → черновик формы */
export function draftFromOverrides(overrides) {
    const o = overrides || {}
    const casters = (Array.isArray(o.casters) ? o.casters : []).map((c) => norm('name', c))
    while (casters.length < CASTER_SLOTS) casters.push('')
    return {
        title: norm('title', o.title),
        casters,
        teams: entitiesDraft(o.teams, ENTITY_FIELDS.teams),
        players: entitiesDraft(o.players, ENTITY_FIELDS.players),
        heroes: entitiesDraft(o.heroes, ENTITY_FIELDS.heroes),
    }
}

/**
 * Что отправить в PATCH {overrides}, чтобы на бэке стал черновик: только изменённые поля,
 * снятые — null; сущность без единой правки — null целиком. Пустой объект — менять нечего.
 */
export function overridesPatch(saved, draft) {
    const base = draftFromOverrides(saved)
    const next = draft || {}
    const patch = {}

    const title = norm('title', next.title)
    if (title !== base.title) patch.title = title || null

    const casters = (next.casters || []).map((c) => norm('name', c)).filter(Boolean)
    if (casters.join('\n') !== base.casters.filter(Boolean).join('\n')) patch.casters = casters

    Object.entries(ENTITY_FIELDS).forEach(([kind, fields]) => {
        const before = base[kind]
        const after = next[kind] || {}
        const out = {}
        new Set([...Object.keys(before), ...Object.keys(after)]).forEach((key) => {
            const b = before[key] || {}
            const a = after[key] || {}
            const changed = {}
            fields.forEach((f) => {
                const value = norm(f, a[f])
                if (value !== norm(f, b[f])) changed[f] = isSet(f, value) ? value : null
            })
            if (!Object.keys(changed).length) return
            out[key] = fields.some((f) => isSet(f, norm(f, a[f]))) ? changed : null
        })
        if (Object.keys(out).length) patch[kind] = out
    })
    return patch
}

export function hasChanges(patch) {
    return !!patch && Object.keys(patch).length > 0
}

/**
 * Предпросмотр того, что нарисует OBS: display у сторон и игроков и подмена картинок героев,
 * как это делает бэк. Нужен для превью ещё не выведенной игры — у неё display с бэка нет.
 */
export function applyOverrides(battle, overrides) {
    if (!battle) return battle
    const o = overrides || {}
    const heroImage = (hero) => {
        const image = hero && o.heroes?.[hero.id]?.image
        return image ? {...hero, avatar: image} : hero
    }
    const heroList = (list) => (Array.isArray(list) ? list.map(heroImage) : list)

    const camps = (battle.camps || []).map((c) => {
        const t = (c.team_id && o.teams?.[c.team_id]) || {}
        return {
            ...c,
            ban_heroes: heroList(c.ban_heroes),
            pick_heroes: heroList(c.pick_heroes),
            display: {
                name: t.name || c.team?.name || c.in_game_team_name || null,
                short_name: t.short_name || c.in_game_team_short_name || null,
                logo: t.logo || logoUrl(c.team?.logo) || null,
            },
        }
    })

    const players = (battle.players || []).map((p) => {
        const ov = (p.role_id && o.players?.[p.role_id]) || {}
        const hero = heroImage(p.hero)
        let shown = hero ? {id: hero.id, name: hero.name, avatar: hero.avatar} : null
        const picked = ov.hero_id && heroCatalog.byOwnId[ov.hero_id]
        if (picked) shown = heroImage({id: picked.id, name: picked.name, avatar: picked.avatar})
        return {
            ...p,
            hero,
            ban_hero: heroImage(p.ban_hero),
            display: {
                name: ov.name || p.name || null,
                photo: ov.photo || logoUrl(p.user?.avatar) || null,
                hero: shown,
            },
        }
    })

    return {...battle, camps, players}
}
